import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Eye, Loader2, Sparkles, Mail, Send, TrendingUp, TrendingDown, Target, AlertCircle, Smartphone } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * InvestorTelemetryChip — compact "dwell time" badge for an investor card.
 *
 * Hits  GET /api/vault/investors/{id}/telemetry
 * Shows total visits, total dwell time, and a "last seen Xm ago" line.
 * Click to expand the per-tab breakdown.
 */

const PALETTE = {
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  text: '#2A2235',
  muted: '#5C526A',
  cream: '#FAFAED',
  border: 'rgba(92,82,106,0.10)',
};

const fmtSecs = (s) => {
  if (!s) return '0s';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return `${Math.floor(diffMin / 1440)}d ago`;
  } catch {
    return '';
  }
};

const TAB_LABELS = {
  'data-room': 'Data Room',
  'active-brief': 'Active Brief',
  'growth-simulator': 'Growth Sim',
  'cost-replicate': 'Cost-to-Replicate',
  'investor-portal': 'Investor Portal',
};

/**
 * SparkBar — compact 7-day activity micro-chart for an investor card.
 *
 * Renders one bar per day for the last 7 UTC days (oldest → newest). Bar
 * height scales to the max-second day in the window. Today's bar is
 * highlighted. A momentum chip next to the bars surfaces the % change
 * in dwell time vs. the prior 7-day window — surging vs cooling at a
 * glance, mapped to four tiers:
 *   ≥+25%   surging  (mint)
 *   +5..+25 warming  (coral)
 *   -5..+5  steady   (lavender)
 *   <-5%    cooling  (muted gray)
 */
const SparkBar = ({ daily, momentumDeltaPct, investorId }) => {
  if (!daily || daily.length === 0) return null;
  const max = Math.max(1, ...daily.map((d) => d.seconds || 0));
  const todayIdx = daily.length - 1;
  const totalRecent = daily.reduce((s, d) => s + (d.seconds || 0), 0);
  if (totalRecent === 0 && (momentumDeltaPct || 0) === 0) {
    return null;
  }

  const delta = Number(momentumDeltaPct || 0);
  let tier;
  if (delta >= 25) tier = { label: `↑ ${delta}% surging`, bg: '#A3E2C9', fg: '#1F5C44' };
  else if (delta > 5) tier = { label: `↗ ${delta}% warming`, bg: PALETTE.coral + '33', fg: '#9A4A33' };
  else if (delta >= -5) tier = { label: `→ ${delta >= 0 ? '+' : ''}${delta}% steady`, bg: '#E9E4F2', fg: '#5C526A' };
  else tier = { label: `↓ ${delta}% cooling`, bg: '#F2E4E4', fg: '#7A4040' };

  const fmtDay = (iso) => {
    try {
      const d = new Date(iso + 'T00:00:00Z');
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    } catch { return iso; }
  };
  const fmtSecsShort = (s) => {
    if (!s) return '0s';
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  };

  return (
    <div className="mt-1.5 flex items-center gap-2" data-testid={`telemetry-sparkbar-${investorId}`}>
      <div className="flex items-end gap-0.5 h-6" aria-label="Last 7 days dwell time">
        {daily.map((d, i) => {
          const h = Math.max(2, Math.round(((d.seconds || 0) / max) * 22));
          const isToday = i === todayIdx;
          const fill = isToday ? PALETTE.coral : (d.seconds > 0 ? PALETTE.lavender : '#E5E1EC');
          return (
            <div
              key={d.date}
              title={`${fmtDay(d.date)} · ${d.visits} visit${d.visits === 1 ? '' : 's'} · ${fmtSecsShort(d.seconds)}`}
              style={{ width: 5, height: h, background: fill, borderRadius: 1.5 }}
              data-testid={`sparkbar-day-${investorId}-${i}`}
            />
          );
        })}
      </div>
      <span
        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
        style={{ background: tier.bg, color: tier.fg }}
        data-testid={`telemetry-momentum-${investorId}`}
      >
        {tier.label}
      </span>
    </div>
  );
};

export const InvestorTelemetryChip = ({ investorId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trailOpen, setTrailOpen] = useState(false);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!investorId) return;
    setLoading(true);
    try {
      const res = await apiClient.request(`/api/vault/investors/${investorId}/telemetry`);
      setData(res);
    } catch { /* swallow */ }
    setLoading(false);
  }, [investorId]);

  useEffect(() => { load(); }, [load]);

  const loadTrail = useCallback(async () => {
    if (!investorId) return;
    setTrailLoading(true);
    try {
      const res = await apiClient.request(`/api/vault/diligence/investors/${investorId}/trail`);
      setTrail(res);
    } catch { /* swallow */ }
    setTrailLoading(false);
  }, [investorId]);

  const toggleTrail = () => {
    const next = !trailOpen;
    setTrailOpen(next);
    if (next && trail === null) loadTrail();
  };

  if (loading && !data) {
    return (
      <div className="inline-flex items-center gap-1 text-xs" style={{ color: PALETTE.muted }} data-testid={`telemetry-loading-${investorId}`}>
        <Loader2 size={10} className="animate-spin" /> Telemetry…
      </div>
    );
  }
  if (!data || data.total_visits === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid={`telemetry-empty-${investorId}`}>
        <Eye size={9} /> No vault activity yet
      </span>
    );
  }

  return (
    <div className="mt-1.5" data-testid={`telemetry-chip-${investorId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold transition-transform hover:scale-[1.02]"
        style={{ background: PALETTE.coral + '22', color: '#9A4A33', border: `1px solid ${PALETTE.coral}55` }}
        data-testid={`telemetry-toggle-${investorId}`}
      >
        <Activity size={10} />
        {data.total_visits} visit{data.total_visits === 1 ? '' : 's'} · {fmtSecs(data.total_seconds)}
        {data.last_seen_at && <span style={{ color: PALETTE.muted, fontWeight: 500 }}>· {fmtTs(data.last_seen_at)}</span>}
      </button>
      <SparkBar daily={data.daily_7d} momentumDeltaPct={data.momentum_delta_pct} investorId={investorId} />
      {open && (
        <div className="mt-2 rounded-lg p-2.5" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}` }} data-testid={`telemetry-breakdown-${investorId}`}>
          <div className="space-y-1.5">
            {data.by_tab.map((t) => {
              const pct = Math.min(100, (t.total_seconds / Math.max(1, data.total_seconds)) * 100);
              return (
                <div key={t.tab} data-testid={`telemetry-tab-${t.tab}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span style={{ color: PALETTE.text }}>
                      <strong>{TAB_LABELS[t.tab] || t.tab}</strong> · {t.visits}× · {fmtSecs(t.total_seconds)}
                    </span>
                    <span className="text-xs" style={{ color: PALETTE.muted }}>{fmtTs(t.last_seen_at)}</span>
                  </div>
                  <div className="h-1 mt-0.5 rounded-full overflow-hidden" style={{ background: '#fff' }}>
                    <div className="h-full" style={{ width: `${pct}%`, background: PALETTE.coral }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sprint 172 Part 2 — Diligence Trail (per-file footprint). */}
          <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${PALETTE.border}` }}>
            <button
              type="button"
              onClick={toggleTrail}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.10em]"
              style={{ color: '#9A4A33' }}
              data-testid={`diligence-trail-toggle-${investorId}`}
            >
              <Eye size={10} /> Diligence Trail {trailOpen ? '▾' : '▸'}
            </button>
            {trailOpen && (
              <div className="mt-2" data-testid={`diligence-trail-${investorId}`}>
                {trailLoading && (
                  <div className="text-xs flex items-center gap-1.5" style={{ color: PALETTE.muted }} data-testid={`diligence-trail-loading-${investorId}`}>
                    <Loader2 size={10} className="animate-spin" /> Loading trail…
                  </div>
                )}
                {!trailLoading && trail && trail.total_files_touched === 0 && (
                  <p className="text-xs" style={{ color: PALETTE.muted }} data-testid={`diligence-trail-empty-${investorId}`}>
                    No diligence files opened yet.
                  </p>
                )}
                {!trailLoading && trail && trail.total_files_touched > 0 && (
                  <>
                    <div className="text-[10px] mb-1.5" style={{ color: PALETTE.muted }}>
                      {trail.total_files_touched} file{trail.total_files_touched === 1 ? '' : 's'} touched · {fmtSecs(trail.total_seconds)} · {trail.total_downloads} download{trail.total_downloads === 1 ? '' : 's'}
                    </div>
                    <ul className="space-y-1.5">
                      {trail.trail.map((row) => {
                        const pct = Math.min(100, (row.view_seconds / Math.max(1, trail.trail[0].view_seconds)) * 100);
                        return (
                          <li key={row.file_id} data-testid={`diligence-trail-row-${row.file_id}`}>
                            <div className="flex items-center justify-between text-xs gap-2">
                              <span className="min-w-0 truncate" style={{ color: PALETTE.text }}>
                                <strong>{row.label}</strong>
                              </span>
                              <span className="flex-shrink-0 text-[10px] font-bold" style={{ color: '#9A4A33' }}>
                                {row.download_count > 0 && <span title="downloads">⬇ {row.download_count} · </span>}
                                {fmtSecs(row.view_seconds)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-[10px]" style={{ color: PALETTE.muted }}>
                              <span>{row.folder_label}</span>
                              <span>{fmtTs(row.last_seen_at)}</span>
                            </div>
                            <div className="h-1 mt-0.5 rounded-full overflow-hidden" style={{ background: '#fff' }}>
                              <div className="h-full" style={{ width: `${pct}%`, background: PALETTE.lavender }} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * VaultTelemetryLeaderboard — Top 10 investors by dwell time.
 * Use on the Vault Overview tab next to the Velocity Heartbeat for a
 * "Who's circling the Vault right now?" view.
 */
export const VaultTelemetryLeaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await apiClient.request('/api/vault/investor-telemetry/overview');
        if (!cancelled) setData(res);
      } catch { /* swallow */ }
      if (!cancelled) setLoading(false);
    };
    fetchIt();
    const id = setInterval(fetchIt, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="telemetry-leaderboard-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading vault telemetry…
      </div>
    );
  }

  const rows = data?.top_investors || [];

  return (
    <div
      className="rounded-2xl p-5 lg:p-6"
      style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.coral}` }}
      data-testid="telemetry-leaderboard"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.coral + '22' }}>
          <Sparkles size={16} style={{ color: '#9A4A33' }} />
        </div>
        <div>
          <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>Who&apos;s circling the Vault</h3>
          <p className="text-xs" style={{ color: PALETTE.muted }}>Top 10 by dwell time · live</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs px-3 py-3 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="telemetry-leaderboard-empty">
          No investor sessions recorded yet. Share an Active Investor passcode and watch this fill.
        </p>
      ) : (
        <ol className="space-y-1.5">
          {rows.map((r, i) => (
            <li
              key={r.investor_id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg"
              style={{ background: PALETTE.cream }}
              data-testid={`telemetry-leader-${r.investor_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-black w-5 text-center" style={{ color: PALETTE.muted }}>{i + 1}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: PALETTE.text }}>
                    {r.name || r.email || r.investor_id}
                  </div>
                  <div className="text-xs truncate" style={{ color: PALETTE.muted }}>
                    {r.firm ? `${r.firm} · ` : ''}{r.session_count} session{r.session_count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-black tabular-nums" style={{ color: PALETTE.coral }}>{fmtSecs(r.total_seconds)}</div>
                <div className="text-xs" style={{ color: PALETTE.muted }}>{fmtTs(r.last_seen_at)}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default InvestorTelemetryChip;

/**
 * MondayTelemetryDigestCard — preview + on-demand send for the weekly
 * Postmark digest. Renders the current week's surging/cooling rosters
 * plus the deepest dwell session. The "Send to my inbox now" button
 * forces a Postmark dispatch outside the Monday 08:00 UTC schedule.
 */
// Module-scoped Row (previously defined inside MondayTelemetryDigestCard,
// which remounted every surging/cooling entry on every parent re-render).
const DigestRow = ({ s, tone }) => {
  const ToneIcon = tone === 'surging' ? TrendingUp : TrendingDown;
  const color = tone === 'surging' ? '#1F5C44' : '#7A4040';
  const bg = tone === 'surging' ? '#E8F6EF' : '#F7E8E8';
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg" style={{ background: bg }} data-testid={`digest-${tone}-${s.investor_id}`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: PALETTE.text }}>{s.name || s.email || s.investor_id}</div>
        {s.firm && <div className="text-xs truncate" style={{ color: PALETTE.muted }}>{s.firm}</div>}
      </div>
      <div className="flex items-center gap-1 text-xs font-black flex-shrink-0" style={{ color }}>
        <ToneIcon size={12} /> {s.momentum_delta_pct >= 0 ? '+' : ''}{s.momentum_delta_pct}%
      </div>
    </li>
  );
};

export const MondayTelemetryDigestCard = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.request('/api/vault/telemetry-digest/preview');
      setPayload(res);
    } catch { /* swallow */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true);
    setSendStatus(null);
    try {
      const res = await apiClient.request('/api/vault/telemetry-digest/send', { method: 'POST', body: JSON.stringify({ force: true }) });
      setSendStatus(res.sent ? `Sent to ${res.recipient}` : `Skipped: ${res.reason}`);
    } catch (e) {
      setSendStatus(`Error: ${e.message || 'send failed'}`);
    }
    setSending(false);
  };

  if (loading && !payload) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="monday-digest-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Composing Monday digest…
      </div>
    );
  }

  const surging = payload?.surging || [];
  const cooling = payload?.cooling || [];
  const deepest = payload?.deepest_session;

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.lavender}` }} data-testid="monday-digest-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.lavender + '22' }}>
            <Mail size={16} style={{ color: '#3F4D7A' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>Monday Telemetry Digest</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>{payload?.week ? `Preview for ${payload.week}` : 'Auto-sent every Monday 08:00 UTC'}</p>
          </div>
        </div>
        <button
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full disabled:opacity-60"
          style={{ background: PALETTE.coral, color: '#fff' }}
          data-testid="monday-digest-send-now"
        >
          <Send size={11} /> {sending ? 'Sending…' : 'Send to my inbox now'}
        </button>
      </div>
      {sendStatus && (
        <p className="text-xs mb-3" style={{ color: PALETTE.muted }} data-testid="monday-digest-send-status">{sendStatus}</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color: '#1F5C44' }}>↑ Surging</div>
          {surging.length === 0 ? (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="digest-surging-empty">No surging VCs this week.</p>
          ) : (
            <ul className="space-y-1.5">{surging.map(s => <DigestRow key={s.investor_id} s={s} tone="surging" />)}</ul>
          )}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5" style={{ color: '#7A4040' }}>↓ Cooling</div>
          {cooling.length === 0 ? (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="digest-cooling-empty">No cooling VCs this week.</p>
          ) : (
            <ul className="space-y-1.5">{cooling.map(s => <DigestRow key={s.investor_id} s={s} tone="cooling" />)}</ul>
          )}
        </div>
      </div>
      <div className="mt-3 rounded-lg p-3" style={{ background: '#FFF9F2', border: '1px solid rgba(249,155,125,0.35)' }} data-testid="digest-deepest">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: '#9A4A33' }}>Deepest dwell · last 7 days</div>
        {deepest ? (
          <div className="mt-1">
            <div className="text-sm font-bold" style={{ color: PALETTE.text }}>{deepest.name || deepest.email || 'Unknown VC'}</div>
            {deepest.firm && <div className="text-xs" style={{ color: PALETTE.muted }}>{deepest.firm}</div>}
            <div className="text-xs mt-1" style={{ color: PALETTE.text }}>
              <strong>{deepest.tab_label || deepest.tab}</strong> · <span style={{ color: '#9A4A33', fontWeight: 700 }}>{fmtSecs(deepest.duration_seconds)}</span> in one session
            </div>
          </div>
        ) : (
          <p className="text-xs mt-1" style={{ color: PALETTE.muted }}>No qualifying deep-dive sessions yet.</p>
        )}
      </div>
    </div>
  );
};


/**
 * ConversionFunnelRibbon — "Reviewed → Sent" deal-pacing conscience.
 *
 * Hits  GET /api/vault/conversion-funnel?window_days=30
 * Surfaces:
 *   • The single conversion %
 *   • Reviewed / Sent absolute counts
 *   • The TOP unconverted VCs (data-room visitors with NO contract dispatch
 *     in the same window) — one click fires the same 'hibff:vault:switch-tab'
 *     + 'hibff:contract-engine:open-draft' handshake the RecommendOfferButton
 *     uses so the founder can close the loop without leaving the ribbon.
 *
 * Auto-refreshes every 60s so the metric tracks live as the founder
 * dispatches new contracts.
 */
export const ConversionFunnelRibbon = ({ windowDays = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommending, setRecommending] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.request(`/api/vault/conversion-funnel?window_days=${windowDays}`);
      setData(res);
    } catch { /* swallow */ }
    setLoading(false);
  }, [windowDays]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const recommendFor = async (investorId, name) => {
    setRecommending(investorId);
    try {
      const res = await apiClient.request(`/api/vault/contracts/recommend/${investorId}`);
      window.dispatchEvent(new CustomEvent('hibff:vault:switch-tab', { detail: { tab: 'offers' } }));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('hibff:contract-engine:open-draft', {
          detail: {
            template_key: res.draft.template_key,
            variables: res.draft.variables,
            signers: res.draft.signers,
            reason: res.draft.reason,
            note: `Recommended for ${name || res.investor?.name || investorId}. ${res.draft.reason || ''}`.trim(),
          },
        }));
      }, 60);
    } catch { /* swallow */ }
    setRecommending(null);
  };

  if (loading && !data) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="conversion-funnel-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading conversion funnel…
      </div>
    );
  }

  const reviewed = data?.reviewed_count || 0;
  const sent = data?.sent_count || 0;
  const pct = data?.conversion_pct || 0;
  const gap = data?.gap || [];
  // Tier the ratio: < 15 cold, 15-39 warming, 40-69 healthy, ≥ 70 elite.
  let tier;
  if (reviewed === 0) tier = { label: 'No reviews yet', bg: '#E9E4F2', fg: '#5C526A', barFg: PALETTE.lavender };
  else if (pct >= 70) tier = { label: 'Elite', bg: '#A3E2C9', fg: '#1F5C44', barFg: '#1F5C44' };
  else if (pct >= 40) tier = { label: 'Healthy', bg: '#D2EDDB', fg: '#1F5C44', barFg: '#3F8B5C' };
  else if (pct >= 15) tier = { label: 'Warming', bg: PALETTE.coral + '33', fg: '#9A4A33', barFg: PALETTE.coral };
  else tier = { label: 'Cold', bg: '#F2E4E4', fg: '#7A4040', barFg: '#B25555' };

  const fmtTs = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
      return `${Math.floor(diffMin / 1440)}d ago`;
    } catch { return ''; }
  };
  const fmtSecs = (s) => {
    if (!s) return '0s';
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  };

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.coral}` }} data-testid="conversion-funnel-ribbon">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.coral + '22' }}>
            <Target size={16} style={{ color: '#9A4A33' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>Reviewed → Sent</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>Rolling {data?.window_days || windowDays}-day deal-pacing index</p>
          </div>
        </div>
        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full" style={{ background: tier.bg, color: tier.fg }} data-testid="conversion-funnel-tier">{tier.label}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div className="rounded-xl p-3" style={{ background: PALETTE.cream }} data-testid="conversion-funnel-pct">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: PALETTE.muted }}>Conversion</div>
          <div className="text-3xl font-black tabular-nums" style={{ color: PALETTE.text }}>{pct}%</div>
        </div>
        <div className="rounded-xl p-3" style={{ background: PALETTE.cream }} data-testid="conversion-funnel-reviewed">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: PALETTE.muted }}>Reviewed</div>
          <div className="text-3xl font-black tabular-nums" style={{ color: PALETTE.text }}>{reviewed}</div>
          <div className="text-[10px]" style={{ color: PALETTE.muted }}>distinct VCs · data-room visits</div>
        </div>
        <div className="rounded-xl p-3" style={{ background: PALETTE.cream }} data-testid="conversion-funnel-sent">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: PALETTE.muted }}>Sent</div>
          <div className="text-3xl font-black tabular-nums" style={{ color: PALETTE.text }}>{sent}</div>
          <div className="text-[10px]" style={{ color: PALETTE.muted }}>contracts dispatched</div>
        </div>
      </div>

      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: PALETTE.cream }}>
        <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: tier.barFg }} data-testid="conversion-funnel-bar" />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: '#9A4A33' }}>
            <AlertCircle size={11} className="inline -mt-0.5 mr-1" /> Gap — VCs reviewed but not yet pitched
          </div>
          <span className="text-xs" style={{ color: PALETTE.muted }}>{gap.length} of {Math.max(0, reviewed - sent)} unconverted</span>
        </div>
        {gap.length === 0 ? (
          <p className="text-xs px-3 py-3 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="conversion-funnel-gap-empty">
            {reviewed === 0 ? 'No data-room reviews yet. Share an investor passcode to start the funnel.' : 'Zero gaps — every VC who visited has received a contract. Closer-mode engaged.'}
          </p>
        ) : (
          <ol className="space-y-1.5" data-testid="conversion-funnel-gap">
            {gap.map((g, i) => (
              <li key={g.investor_id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg" style={{ background: PALETTE.cream }} data-testid={`conversion-funnel-gap-row-${g.investor_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-black w-5 text-center" style={{ color: PALETTE.muted }}>{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: PALETTE.text }}>{g.name || g.email || g.investor_id}</div>
                    <div className="text-xs truncate" style={{ color: PALETTE.muted }}>{g.firm ? `${g.firm} · ` : ''}{fmtSecs(g.total_seconds)} dwell · {fmtTs(g.last_seen_at)}</div>
                  </div>
                </div>
                <button
                  onClick={() => recommendFor(g.investor_id, g.name)}
                  disabled={recommending === g.investor_id}
                  className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full disabled:opacity-60"
                  style={{ background: PALETTE.coral, color: '#fff' }}
                  data-testid={`conversion-funnel-recommend-${g.investor_id}`}
                >
                  <Send size={11} /> {recommending === g.investor_id ? '…' : 'Recommend'}
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};





/**
 * VaultDailyPulseCard — preview + on-demand send for the Vault Daily Pulse
 * Slack/Telegram channel. When no channel is configured the backend
 * returns `channels: []` and logs the rendered text — UI surfaces a hint
 * so the founder knows the pipeline is healthy and is only missing creds.
 */
export const VaultDailyPulseCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.request('/api/vault/pulse/preview');
      setData(r);
    } catch { /* swallow */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true); setStatus(null);
    try {
      const r = await apiClient.request('/api/vault/pulse/send', { method: 'POST', body: JSON.stringify({ force: true }) });
      const channels = (r.channels || []).filter(c => c.ok).map(c => c.channel);
      if (channels.length > 0) setStatus(`Sent to: ${channels.join(', ')}`);
      else if (r.reason === 'no_channels_configured') setStatus('Logged only — wire Slack or Telegram env vars to ship to your phone.');
      else setStatus(`No channel succeeded · ${(r.channels || []).map(c => `${c.channel}:${c.reason || (c.ok ? 'ok' : 'fail')}`).join(' · ')}`);
    } catch (e) {
      setStatus(`Error: ${e.message || 'send failed'}`);
    }
    setSending(false);
  };

  if (loading && !data) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="vault-pulse-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Composing Daily Pulse…
      </div>
    );
  }

  const channels = data?.channels_configured || [];
  const text = data?.text || '';

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.mint}` }} data-testid="vault-pulse-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.mint + '55' }}>
            <Smartphone size={16} style={{ color: '#1F5C44' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>Vault Daily Pulse</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>
              Auto-fires once daily ·{' '}
              {channels.length === 0
                ? <span className="font-semibold" style={{ color: '#9A4A33' }}>No channels — wire Slack or Telegram env vars to enable</span>
                : <span className="font-semibold" style={{ color: '#1F5C44' }}>{channels.join(' + ')} ready</span>}
            </p>
          </div>
        </div>
        <button
          onClick={send}
          disabled={sending}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full disabled:opacity-60"
          style={{ background: PALETTE.coral, color: '#fff' }}
          data-testid="vault-pulse-send-now"
        >
          <Send size={11} /> {sending ? 'Pinging…' : 'Send to my phone now'}
        </button>
      </div>
      {status && <p className="text-xs mb-3" style={{ color: PALETTE.muted }} data-testid="vault-pulse-status">{status}</p>}
      <pre className="text-xs whitespace-pre-wrap font-mono p-3 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.text }} data-testid="vault-pulse-preview-text">
{text || '(preview empty)'}
      </pre>
    </div>
  );
};
